import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HelpersService } from '../../services/helpers.service';
import { NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';

export interface videoData {
  videoThumbnail: string;
  thumbnailAltText: string;
  transcriptText?: string;
  videoSrc: string;
  safeVideoSrc?: SafeResourceUrl;
  playing: boolean;
}

@Component({
  selector: '[video-carousel]',
  imports: [CommonModule, NgbAccordionModule],
  templateUrl: './video-carousel.component.html',
  styleUrl: './video-carousel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoCarouselComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  currentVideo!: videoData;
  currentTranscript = 0;

  @Input()
  transcript: boolean = false;

  @ViewChild('videoFrame') videoFrame?: ElementRef<HTMLIFrameElement>;
  player: any;
  private playerReady = false;
  private pendingVideoId: string | null = null;

  @ViewChild('scrollContainer') scrollContainer?: ElementRef<HTMLElement>;

  scrollAmount: number = 0;
  atStart: boolean = true;
  atEnd: boolean = false;

  @Input() videoCarouselData: videoData[] = [];
  iframeRenderId = 0;

  constructor(
    private sanitizer: DomSanitizer,
    private helpersService: HelpersService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  getCurrentYoutubeUrl(): string {
    if (!this.currentVideo) return '';
    const videoId = this.extractVideoId(this.currentVideo.videoSrc);
    return videoId ? `https://www.youtube.com/watch?v=${videoId}` : '';
  }

  ngOnInit(): void {
    if (this.videoCarouselData.length === 0) return;

    const playingIndex = this.videoCarouselData.findIndex(
      (video) => video.playing
    );
    const index = playingIndex >= 0 ? playingIndex : 0;
    this.markPlaying(index);
    this.setCurrentVideo(this.videoCarouselData[index]);
  }

  ngAfterViewInit() {
    this.calculateScrollAmount();
    this.checkScroll();

    if (!this.helpersService.validatePlatform()) return;
    if (this.isAutoplayRoute()) {
      this.loadYouTubeApi();
    }
  }

  ngOnDestroy(): void {
    this.player = null;
    this.playerReady = false;
  }

  private isAutoplayRoute(): boolean {
    const url = this.router.url.split('?')[0];
    return url === '/take-the-pledge' || url === '/make-a-pledge';
  }

  private loadYouTubeApi() {
    if ((window as any).YT?.Player) {
      this.initPlayer();
      return;
    }

    const existingScript = this.helpersService.doc.querySelector(
      "script[src='https://www.youtube.com/iframe_api']"
    );
    if (!existingScript) {
      const tag = this.helpersService.doc.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      this.helpersService.doc.body.appendChild(tag);
    }

    const previous = (window as any).onYouTubeIframeAPIReady;
    (window as any).onYouTubeIframeAPIReady = () => {
      previous?.();
      this.initPlayer();
    };
  }

  initPlayer() {
    if (this.player || !this.videoFrame?.nativeElement) return;
    if (!(window as any).YT?.Player) return;

    this.player = new (window as any).YT.Player(this.videoFrame.nativeElement, {
      events: {
        onReady: () => {
          this.playerReady = true;
          if (this.pendingVideoId) {
            this.loadAndPlay(this.pendingVideoId);
            this.pendingVideoId = null;
          } else if (this.isAutoplayRoute()) {
            this.player.mute();
            this.player.playVideo();
          }
          this.cdr.markForCheck();
        },
      },
    });
  }

  private extractVideoId(videoSrc: string): string {
    if (!videoSrc) return '';
    const match = videoSrc.match(/embed\/([\w-]+)/);
    if (match?.[1]) return match[1];
    const urlMatch = videoSrc.match(/[?&]v=([\w-]+)/);
    return urlMatch?.[1] ?? '';
  }

  private appendParam(videoSrc: string, param: string): string {
    if (!videoSrc || videoSrc.includes(param)) return videoSrc;
    return videoSrc.includes('?') ? `${videoSrc}&${param}` : `${videoSrc}?${param}`;
  }

  private withEnableJsApi(videoSrc: string): string {
    return this.appendParam(videoSrc, 'enablejsapi=1');
  }

  private withAutoplay(videoSrc: string): string {
    return this.appendParam(videoSrc, 'autoplay=1');
  }

  setCurrentVideo(video: videoData) {
    const iframeSrc = this.player
      ? this.currentVideo?.safeVideoSrc
      : video.videoSrc
        ? this.sanitizer.bypassSecurityTrustResourceUrl(
            this.withEnableJsApi(video.videoSrc)
          )
        : undefined;

    this.currentVideo = {
      ...video,
      safeVideoSrc: iframeSrc,
    };
  }

  private markPlaying(index: number) {
    this.videoCarouselData.forEach((thumbnail, i) => {
      thumbnail.playing = i === index;
    });
    this.currentTranscript = index;
    this.cdr.markForCheck();
  }

  private loadAndPlay(videoId: string) {
    if (!this.player?.loadVideoById) return;
    this.player.loadVideoById(videoId);
    setTimeout(() => {
      this.player?.unMute?.();
      this.player?.playVideo?.();
    }, 300);
  }

  calculateScrollAmount() {
    if (this.scrollContainer?.nativeElement) {
      const el = this.scrollContainer.nativeElement;
      this.scrollAmount = el.scrollWidth / 3;
    }
  }

  scrollLeft() {
    if (!this.scrollContainer?.nativeElement) {
      return;
    }
    this.scrollContainer.nativeElement.scrollBy({
      left: -this.scrollAmount,
      behavior: 'smooth',
    });
  }

  scrollRight() {
    if (!this.scrollContainer?.nativeElement) {
      return;
    }
    this.scrollContainer.nativeElement.scrollBy({
      left: this.scrollAmount,
      behavior: 'smooth',
    });
  }

  checkScroll() {
    if (!this.scrollContainer?.nativeElement) {
      return;
    }
    const el = this.scrollContainer.nativeElement;
    this.atStart = el.scrollLeft <= 0;
    this.atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    this.cdr.markForCheck();
  }

  playVideo(index: number) {
    const selected = this.videoCarouselData[index];
    if (!selected) return;

    this.markPlaying(index);

    if (!selected.videoSrc) {
      this.currentVideo = { ...selected, safeVideoSrc: undefined };
      this.cdr.markForCheck();
      return;
    }

    const videoId = this.extractVideoId(selected.videoSrc);

    if (this.playerReady && videoId) {
      this.setCurrentVideo(selected);
      this.loadAndPlay(videoId);
      return;
    }

    if (this.player && videoId) {
      this.pendingVideoId = videoId;
      this.setCurrentVideo(selected);
      return;
    }

    this.iframeRenderId++;
    this.currentVideo = {
      ...selected,
      safeVideoSrc: this.sanitizer.bypassSecurityTrustResourceUrl(
        this.withAutoplay(this.withEnableJsApi(selected.videoSrc))
      ),
    };
    this.cdr.markForCheck();
  }
}
